import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect,
} from '../components/ui/ListPageChrome';
import { useTableSort, SortTh } from '../components/ui/TableSort';

const STATUS_COLORS = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#E8F4FE] text-[#2184F2]',
  Completed: 'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/**
 * Did SAP take this transfer?
 *
 * The answer was already in the row — the list endpoint returns sap_sync_status and
 * sap_sync_error — but nothing on this screen showed it. Twenty transfers SAP had
 * refused looked exactly like the five hundred it accepted, so the people who moved
 * the stock went home believing the job was done.
 *
 * Drafts are not meant to reach SAP, so they are left blank rather than flagged: a
 * warning that is always on is not a warning.
 */
function SapState({ row }) {
  if (row.status === 'Draft' || row.status === 'Cancelled') {
    return <span className="text-sap-textLight">—</span>;
  }
  if (row.sap_doc_num != null || row.sap_doc_entry != null) {
    return (
      <span className="text-[#2E7D32]" title={`Posted in SAP${row.sap_doc_num != null ? ` as ${row.sap_doc_num}` : ''}`}>
        {row.sap_doc_num != null ? row.sap_doc_num : 'Posted'}
      </span>
    );
  }
  if (row.sap_sync_status === 'failed') {
    return (
      <span
        className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#FDF0EE] text-[#C4574B] cursor-help"
        title={row.sap_sync_error || 'SAP refused this transfer.'}
      >
        Refused
      </span>
    );
  }
  return (
    <span className="text-[#B07D2B]" title="Queued for SAP. It has not been posted there yet.">
      Pending
    </span>
  );
}

export default function InventoryTransfers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.get('/inventory-transfers');
        if (!cancelled) {
          setRows(data || []);
          setSelectedIds(new Set());
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Header classes, named because they are now repeated across eight cells.
  const TH = 'text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider';
  const TH_C = 'text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider';

  const filtered = rows.filter((r) => {
    const s = search.toLowerCase();
    const matchSearch = !s
      || (r.transfer_number || '').toLowerCase().includes(s)
      || (r.from_wh_code || '').toLowerCase().includes(s)
      || (r.to_wh_code || '').toLowerCase().includes(s);
    const matchStatus = !statusFilter || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleSelectRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const sort = useTableSort(filtered);

  const handleSelectAll = () => {
    const allSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.transfer_id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        filtered.forEach(r => next.delete(r.transfer_id));
      } else {
        filtered.forEach(r => next.add(r.transfer_id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const msg = `Are you sure you want to delete the ${selectedIds.size} selected inventory transfer(s)? This will also revert stock movements.`;
    if (!window.confirm(msg)) return;

    try {
      await api.post('/inventory-transfers/bulk-delete', {
        ids: Array.from(selectedIds)
      });
      setSelectedIds(new Set());
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert(err.message || 'Failed to delete inventory transfers');
    }
  };

  return (
    <ListPageShell>
      <ListPageHeader
        title="Inventory Transfer"
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && selectedIds.size > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({selectedIds.size})
              </button>
            )}
            <button type="button" onClick={() => navigate('/inventory/transfer/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <ListSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by transfer number or warehouse…"
        />
        <ListFilterSelect value={statusFilter} onChange={setStatusFilter}>
          <option value="">All Status</option>
          {['Draft', 'Open', 'Completed', 'Cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
        </ListFilterSelect>
      </div>

      <div className="data-table-wrap">
        {loading ? (
          <div className="py-12 text-center text-sm text-sap-textLight">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head">
                {isSuperAdmin && (
                  <th className="text-center px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every(r => selectedIds.has(r.transfer_id))}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">#</th>
                <SortTh sort={sort} field="transfer_number" className={TH}>Transfer No.</SortTh>
                <SortTh sort={sort} field="posting_date" className={TH}>Posting Date</SortTh>
                <SortTh sort={sort} field="from_wh_code" className={TH}>From Warehouse</SortTh>
                <SortTh sort={sort} field="to_wh_code" className={TH}>To Warehouse</SortTh>
                <SortTh sort={sort} field="line_count" className={TH_C} align="center">Lines</SortTh>
                <SortTh sort={sort} field="status" className={TH_C} align="center">Status</SortTh>
                <SortTh sort={sort} field="sap_sync_status" className={TH_C} align="center">In SAP</SortTh>
              </tr>
            </thead>
            <tbody>
              {sort.rows.length === 0 ? (
                <tr><td colSpan={isSuperAdmin ? 9 : 8} className="px-4 py-8 text-center text-sap-textLight">No transfers found</td></tr>
              ) : sort.rows.map((r, i) => (
                <tr
                  key={r.transfer_id}
                  onClick={() => navigate(`/inventory/transfer/${r.transfer_id}`)}
                  className="table-row even:bg-gray-50/50"
                >
                  {isSuperAdmin && (
                    <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.transfer_id)}
                        onChange={() => handleSelectRow(r.transfer_id)}
                        className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-sap-textLight">{i + 1}</td>
                  <td className="px-4 py-3 font-semibold text-sap-blue">{r.transfer_number}</td>
                  <td className="px-4 py-3 text-sap-textLight">{fmtDate(r.posting_date)}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono">{r.from_wh_code || '—'}</span>
                    {r.from_wh_name && <span className="text-sap-textLight"> — {r.from_wh_name}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono">{r.to_wh_code || '—'}</span>
                    {r.to_wh_name && <span className="text-sap-textLight"> — {r.to_wh_name}</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sap-textLight">{r.line_count || 0}</td>
                  <td className="px-4 py-3 text-center">
                    {r.status === 'Open' ? (
                      <span className="badge-active">{r.status}</span>
                    ) : (
                      <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${STATUS_COLORS[r.status] || ''}`}>{r.status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center"><SapState row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <p className="text-xs text-sap-textLight">{filtered.length} transfer{filtered.length !== 1 ? 's' : ''}</p>
      )}
    </ListPageShell>
  );
}
